import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from "@angular/router";
import {Observable} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../../store/state/app.state";
import {environment} from "../../../environments/environment";
import {Order, StandardName} from "../../store/state/order.state";
import {orderSelector} from "../../store/state/state.selectors";
import {mergeMap, tap} from "rxjs/operators";
import {AdditionalQuestionsData, OrderActions} from "../../store/actions/order.actions";
import {ClientOrderResolver} from "./client-order-resolver.service";
import {EndpointsProvider} from "../../umbrella-common/endpoints/endpoint-provider.service";

@Injectable()
export class AdditionalQuestionsResolver implements Resolve<AdditionalQuestionsData> {

	private order$: Observable<Order>;

	constructor(private endpoints: EndpointsProvider, private store: Store<AppState>, private orderResolver: ClientOrderResolver) {
		this.order$ = this.store.select(orderSelector);
	}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<AdditionalQuestionsData> {
		const skipResolvers = route.queryParams['skipSetup'] === 'true';
		if (skipResolvers) {
			return this.orderResolver.resolve(route, state).pipe(
				mergeMap(order => this.fetchAdditionalQuestionsData(order))
			);
		}

		return this.orderResolver.resolve(route, state).pipe(
			mergeMap(order => this.fetchAdditionalQuestionsData(order)),
			tap(data => this.store.dispatch(OrderActions.SETUP_ADDITIONAL_QUESTIONS({data})))
		);
	}

	private fetchAdditionalQuestionsData(order: Order): Observable<AdditionalQuestionsData> {
		const standards: string = order.products.map(p => p.standard.name).join(',');
		const url: string = `${environment.apiUrl}/order/additional-questions-data?standards=${standards}`;
		return this.endpoints.client.getAdditionalQuestionsDataFor(order).pipe(
			tap(data => {
				if (this.hasBothIso27Products(data)) {
					this.removeCommonQuestions(data)
				}
			})
		);
	}

	private hasBothIso27Products(data: AdditionalQuestionsData): boolean {
		return data.productData.filter(pd => [StandardName.ISO_27001, StandardName.ISO_27701].includes(pd.standardName)).length == 2;
	}

	private removeCommonQuestions(data: AdditionalQuestionsData): void {
		let iso27001Questions = data.productData.find(pd => pd.standardName == StandardName.ISO_27001);
		let iso27701Questions = data.productData.find(pd => pd.standardName == StandardName.ISO_27701);

		iso27701Questions.binaryQuestions = iso27701Questions.binaryQuestions.filter(bq => !iso27001Questions.binaryQuestions.some(iso27001Bq => iso27001Bq.type == bq.type))
		iso27701Questions.textQuestions = iso27701Questions.textQuestions.filter(tq => !iso27001Questions.textQuestions.some(iso27001Tq => iso27001Tq.type == tq.type))
	}
}
